import { motion } from "framer-motion";
import { startCase } from "lodash";
import { useEffect, useRef } from "react";
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { FaFireAlt, FaGamepad, FaPaperPlane } from "react-icons/fa";
import styled from "styled-components";

/* ─── Mass-poster battle log / match-info panel ───
   Matches the arena's ink + amber + wine language. Replaces the old
   purple card with an ink-900 surface, amber accent rules, cut corners
   on the input, and color-coded event chips (red = damage, amber = stat). */

const ChatContainer = styled(Card)`
  border: 1px solid var(--color-ink-700);
  border-radius: 0;
  clip-path: var(--clip-card);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, var(--color-ink-900) 0%, var(--color-ink-950) 100%);
  color: var(--color-cream-100);
  font-family: var(--font-body);
`;

const ChatHeader = styled(Card.Header)`
  position: relative;
  background: var(--color-ink-900);
  padding: var(--space-3) var(--space-3) var(--space-2);
  border-bottom: 1px solid var(--color-ink-700);

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 2px;
    background: var(--color-amber-500);
  }
`;

const ChatTitle = styled.h4`
  margin: 0 0 8px;
  font-family: var(--font-heading);
  font-size: var(--size-md);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-cream-100);
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: var(--color-amber-500);
  }

  &::after {
    content: '////';
    font-family: var(--font-display);
    font-style: italic;
    font-size: 0.9em;
    color: var(--color-amber-500);
    letter-spacing: -0.08em;
    margin-left: auto;
  }
`;

const PlayerInfo = styled.p`
  margin: 2px 0;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.02em;
  color: var(--color-cream-300);

  strong {
    color: var(--color-amber-500);
    font-weight: var(--weight-bold);
  }
`;

const GameAlert = styled.p`
  position: relative;
  margin: 10px 0 2px;
  padding: 5px 8px 5px 10px;
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.02em;
  color: var(--color-cream-200);
  background: rgba(193, 39, 45, 0.12);
  border-left: 3px solid var(--color-red-500);

  svg {
    color: var(--color-red-500);
  }
`;

const ChatScrollArea = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--space-3);
  background-color: rgba(8, 7, 10, 0.35);
  scroll-behavior: smooth;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-ink-600);
  }
  &::-webkit-scrollbar-thumb:hover {
    background: var(--color-amber-500);
  }

  .battle-event:last-child {
    margin-bottom: 10px;
  }
`;

const BattleTurn = styled.div`
  margin-bottom: 12px;
`;

const TurnHeader = styled.h6`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 6px;
  padding: 4px 0;
  font-family: var(--font-heading);
  font-size: 11px;
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-widest);
  text-transform: uppercase;
  color: var(--color-amber-500);

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--color-amber-500);
    opacity: 0.45;
  }
`;

const BattleEvent = styled(motion.div)`
  background-color: ${props =>
        props.isHealth ? 'rgba(193, 39, 45, 0.14)' : 'rgba(255, 177, 60, 0.10)'};
  border-left: 3px solid ${props =>
        props.isHealth ? 'var(--color-red-500)' : 'var(--color-amber-500)'};
  margin: 4px 0;
  padding: 6px 10px;
`;

const EventTitle = styled.p`
  margin: 0;
  padding: 0;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: var(--weight-semibold);
  color: var(--color-cream-100);
  line-height: 1.35;
`;

const EventDetail = styled.p`
  margin: 3px 0 0;
  padding: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.01em;
  color: var(--color-cream-300);
`;

const ChatFooter = styled(Card.Footer)`
  padding: var(--space-2) var(--space-3);
  background: var(--color-ink-950);
  border-top: 1px solid var(--color-ink-700);
`;

const ChatInputGroup = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const ChatInput = styled(Form.Control)`
  background-color: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--color-ink-700);
  color: var(--color-cream-100);
  border-radius: 0;
  clip-path: var(--clip-button);
  padding: 8px 40px 8px 12px;
  font-family: var(--font-body);
  font-size: 13px;

  &:focus {
    background-color: rgba(255, 255, 255, 0.06);
    box-shadow: none;
    border-color: var(--color-amber-500);
    color: var(--color-cream-100);
  }

  &::placeholder {
    color: var(--color-cream-400);
    letter-spacing: 0.02em;
  }
`;

const SendButton = styled(Button)`
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: var(--color-amber-500);
  padding: 2px 6px;

  &:hover, &:focus, &:active {
    background: none !important;
    color: var(--color-amber-400) !important;
    box-shadow: none !important;
  }
`;

const ChatLayout = (props) => {
  const { battleLogs: turns } = props;
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      // Smooth scroll to bottom to show new logs
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [props.battleLogs]);

  // Additional effect to ensure we're always at the bottom when new logs arrive
  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    };

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [props.battleLogs]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    // Implement sending chat functionality here
  };

  return (
    <ChatContainer>
      <ChatHeader>
        <ChatTitle>
          <FaGamepad size={16} />
          Battle Log
        </ChatTitle>
        <PlayerInfo>
          Battle between <strong>{props.username}</strong> vs <strong>{props.gameData?.opponent.name}</strong>
        </PlayerInfo>
        <PlayerInfo>
          Your lead: <strong>{props.gameData?.userTeam[0].name}</strong>
        </PlayerInfo>
        <PlayerInfo>
          Opponent lead: <strong>{props.gameData?.opponent.team[0].name}</strong>
        </PlayerInfo>
        <GameAlert>
          <FaFireAlt size={10} style={{ marginRight: 5 }} />
          Battle Timer is ON. Inactive players will lose once timer is up!
        </GameAlert>
      </ChatHeader>

      <ChatScrollArea ref={scrollRef}>
        {turns && Object.keys(turns).map((turn, i) => (
          <BattleTurn key={turn}>
            <TurnHeader>
              {startCase(turn)}
            </TurnHeader>

            {turns[turn].battleLogs.map((log, index) => (
              <BattleEvent
                key={index}
                isHealth={!!log.healthChange}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <EventTitle>{log.title}</EventTitle>

                {log.healthChange && (
                  <EventDetail>{log.healthChange}</EventDetail>
                )}

                {log.effectChange && (
                  log.effectChange.map((effect, idx) => (
                    <EventDetail key={idx}>{effect}</EventDetail>
                  ))
                )}
              </BattleEvent>
            ))}
          </BattleTurn>
        ))}
      </ChatScrollArea>

      <ChatFooter>
        <form onSubmit={handleSendMessage}>
          <Row>
            <Col md={12}>
              <ChatInputGroup>
                <ChatInput
                  type="text"
                  placeholder="Type message..."
                />
                <SendButton type="submit">
                  <FaPaperPlane />
                </SendButton>
              </ChatInputGroup>
            </Col>
          </Row>
        </form>
      </ChatFooter>
    </ChatContainer>
  );
}


export default ChatLayout